<template>
  <component :is="renderNode(node)" />
</template>

<script setup>
import { h, defineProps } from 'vue';

const props = defineProps({
  node: { type: Object, required: true }
});

const SIZE_MAP = {
  xxs: '11px', xs: '13px', sm: '14px', md: '16px',
  lg: '19px', xl: '22px', xxl: '29px', '3xl': '33px',
  '4xl': '37px', '5xl': '41px'
};

const MARGIN_MAP = {
  none: '0', xs: '2px', sm: '4px', md: '8px',
  lg: '12px', xl: '16px', xxl: '20px'
};

const SPACING_MAP = {
  none: '0', xs: '2px', sm: '4px', md: '8px',
  lg: '12px', xl: '16px', xxl: '20px'
};

const CORNER_RADIUS_MAP = {
  none: '0', xs: '2px', sm: '4px', md: '8px',
  lg: '12px', xl: '16px', xxl: '20px'
};

const BORDER_WIDTH_MAP = {
  none: '0', light: '0.5px', normal: '1px',
  medium: '2px', 'semi-bold': '3px', bold: '4px'
};

/** Flex 的漸層背景：angle 與 CSS 同為 0deg 由下往上、順時針遞增 */
function cssBackground(background) {
  if (background?.type !== 'linearGradient') return null;
  const stops = [background.startColor, background.centerColor, background.endColor].filter(Boolean);
  if (stops.length < 2) return null;
  return `linear-gradient(${background.angle || '0deg'}, ${stops.join(', ')})`;
}

function renderNode(n) {
  if (!n) return h('span');
  switch (n.type) {
    case 'box': return renderBox(n);
    case 'text': return renderText(n);
    case 'image': return renderImage(n);
    case 'button': return renderButton(n);
    case 'separator': return renderSeparator(n);
    case 'icon': return renderIcon(n);
    case 'filler': return renderFiller(n);
    default: return h('div', { class: 'flex-unknown' }, `[${n.type}]`);
  }
}

function renderBox(n) {
  const style = {};
  style.display = 'flex';

  if (n.layout === 'horizontal') {
    style.flexDirection = 'row';
  } else if (n.layout === 'baseline') {
    style.flexDirection = 'row';
    style.alignItems = 'baseline';
  } else {
    style.flexDirection = 'column';
  }

  if (n.spacing) style.gap = SPACING_MAP[n.spacing] || n.spacing;
  if (n.margin) style.marginTop = MARGIN_MAP[n.margin] || n.margin;
  if (n.paddingAll) style.padding = n.paddingAll;
  if (n.paddingTop) style.paddingTop = n.paddingTop;
  if (n.paddingBottom) style.paddingBottom = n.paddingBottom;
  if (n.paddingStart) style.paddingLeft = n.paddingStart;
  if (n.paddingEnd) style.paddingRight = n.paddingEnd;
  if (n.backgroundColor) style.background = n.backgroundColor;
  const gradient = cssBackground(n.background);
  if (gradient) style.background = gradient;
  if (n.cornerRadius) {
    style.borderRadius = CORNER_RADIUS_MAP[n.cornerRadius] || n.cornerRadius;
    style.overflow = 'hidden';
  }
  if (n.borderWidth) {
    style.border = `${BORDER_WIDTH_MAP[n.borderWidth] || n.borderWidth} solid ${n.borderColor || '#000000'}`;
    style.boxSizing = 'border-box';
  }
  // 疊圖版型會用 position: absolute + offset 把文字壓在圖片上；父層一律 relative 才定位得到
  style.position = n.position === 'absolute' ? 'absolute' : 'relative';
  if (n.offsetTop) style.top = n.offsetTop;
  if (n.offsetBottom) style.bottom = n.offsetBottom;
  if (n.offsetStart) style.left = n.offsetStart;
  if (n.offsetEnd) style.right = n.offsetEnd;
  if (n.width) style.width = n.width;
  if (n.height) style.height = n.height;
  if (n.maxWidth) style.maxWidth = n.maxWidth;
  if (n.maxHeight) style.maxHeight = n.maxHeight;
  if (n.flex !== undefined) style.flex = n.flex;

  if (n.justifyContent) {
    const jcMap = { center: 'center', 'flex-start': 'flex-start', 'flex-end': 'flex-end', 'space-between': 'space-between', 'space-around': 'space-around', 'space-evenly': 'space-evenly' };
    style.justifyContent = jcMap[n.justifyContent] || n.justifyContent;
  }
  if (n.alignItems) {
    const aiMap = { center: 'center', 'flex-start': 'flex-start', 'flex-end': 'flex-end' };
    style.alignItems = aiMap[n.alignItems] || n.alignItems;
  }

  const children = (n.contents || []).map((child, i) => {
    return h('div', { key: i, style: { flex: child.flex !== undefined ? child.flex : undefined } }, [renderNode(child)]);
  });

  return h('div', { class: 'flex-box', style }, children);
}

function renderText(n) {
  const style = {};
  style.fontSize = SIZE_MAP[n.size] || n.size || '14px';
  style.color = n.color || '#333333';
  style.fontWeight = n.weight === 'bold' ? '700' : '400';
  style.textAlign = n.align || 'left';
  style.lineHeight = '1.5';
  if (!n.wrap) {
    style.overflow = 'hidden';
    style.textOverflow = 'ellipsis';
    style.whiteSpace = 'nowrap';
  } else {
    style.whiteSpace = 'pre-wrap';
    style.wordBreak = 'break-word';
  }
  if (n.margin) style.marginTop = MARGIN_MAP[n.margin] || n.margin;
  if (n.decoration === 'line-through') style.textDecoration = 'line-through';
  if (n.decoration === 'underline') style.textDecoration = 'underline';
  if (n.style === 'italic') style.fontStyle = 'italic';
  if (n.maxLines) {
    style.display = '-webkit-box';
    style.webkitLineClamp = n.maxLines;
    style.webkitBoxOrient = 'vertical';
    style.overflow = 'hidden';
  }
  if (n.flex !== undefined) style.flex = n.flex;
  if (n.gravity === 'bottom') style.alignSelf = 'flex-end';
  if (n.gravity === 'center') style.alignSelf = 'center';

  return h('span', { class: 'flex-text', style }, n.text || '');
}

function renderImage(n) {
  const wrapStyle = {};
  if (n.margin) wrapStyle.marginTop = MARGIN_MAP[n.margin] || n.margin;
  if (n.backgroundColor) wrapStyle.background = n.backgroundColor;

  const imgStyle = { display: 'block', width: '100%' };
  if (n.aspectMode === 'cover') {
    imgStyle.objectFit = 'cover';
  } else {
    imgStyle.objectFit = 'contain';
  }

  if (n.aspectRatio) {
    const [w, ratioH] = n.aspectRatio.split(':').map(Number);
    if (w && ratioH) {
      wrapStyle.aspectRatio = `${w}/${ratioH}`;
      imgStyle.height = '100%';
    }
  }

  const sizeMap = {
    xxs: '40px', xs: '60px', sm: '80px', md: '120px',
    lg: '160px', xl: '200px', full: '100%'
  };
  if (n.size && n.size !== 'full') {
    wrapStyle.width = sizeMap[n.size] || n.size;
    wrapStyle.margin = '0 auto';
  }

  if (n.cornerRadius) imgStyle.borderRadius = n.cornerRadius;

  return h('div', { class: 'flex-image', style: wrapStyle }, [
    n.url ? h('img', { src: n.url, style: imgStyle, alt: '', loading: 'lazy' }) : h('div', { class: 'flex-image-placeholder' }, 'No Image')
  ]);
}

function renderButton(n) {
  const style = {};
  if (n.margin) style.marginTop = MARGIN_MAP[n.margin] || n.margin;

  const btnStyle = {
    display: 'block', width: '100%', padding: n.height === 'sm' ? '6px 12px' : '10px 16px',
    borderRadius: '6px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', textAlign: 'center', border: 'none',
    textDecoration: 'none', boxSizing: 'border-box'
  };

  if (n.style === 'primary') {
    btnStyle.background = n.color || '#06C755';
    btnStyle.color = '#FFFFFF';
  } else if (n.style === 'secondary') {
    btnStyle.background = '#FFFFFF';
    btnStyle.border = '1px solid #D1D5DB';
    btnStyle.color = n.color || '#333333';
  } else {
    btnStyle.background = 'transparent';
    btnStyle.color = n.color || '#3B82F6';
  }

  const label = n.action?.label || 'Button';
  return h('div', { style }, [
    h('div', { class: 'flex-button', style: btnStyle }, label)
  ]);
}

function renderSeparator(n) {
  const style = { borderTop: `1px solid ${n.color || '#E5E7EB'}`, width: '100%' };
  if (n.margin) style.marginTop = MARGIN_MAP[n.margin] || n.margin;
  return h('div', { class: 'flex-separator', style });
}

function renderIcon(n) {
  const size = SIZE_MAP[n.size] || n.size || '16px';
  return h('img', {
    src: n.url, class: 'flex-icon',
    style: { width: size, height: size, objectFit: 'contain' }
  });
}

function renderFiller() {
  return h('div', { class: 'flex-filler', style: { flex: 1 } });
}
</script>

<style scoped>
.flex-box { min-width: 0; }
.flex-text { min-width: 0; }
.flex-image img { max-width: 100%; }
.flex-image-placeholder {
  width: 100%; height: 120px;
  display: flex; align-items: center; justify-content: center;
  background: #f3f4f6; color: #9ca3af; font-size: 12px;
}
.flex-button { transition: opacity .15s; }
.flex-button:hover { opacity: .85; }
.flex-unknown { color: #ef4444; font-size: 11px; }
</style>
